import React, { useState } from "react";
import { toast } from "sonner";
import { postTracerLoad, postTracerLoadForce, postTracerLoadTest } from "@client/api/tracer";
import { t } from "@client/lib/dictionary";
import { showWriteResult } from "@client/lib/verifyToast";
import { useUserStore } from "@client/store/user";
import type { TracerTelemetry } from "@client/types/tracer";
import { Button } from "@client/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@client/components/ui/card";

interface Props {
  slug: string;
  telemetry: TracerTelemetry | undefined;
}

const errMsg = (err: unknown): string =>
  String((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? err);

export const TracerControls: React.FC<Props> = ({ slug, telemetry }) => {
  const user = useUserStore((s) => s.user);
  const isAdmin = typeof user === "object" && user !== null && (user as { role: string }).role === "ADMIN";
  const [busy, setBusy] = useState(false);

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.tracer.detail.controls}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">{t.tracer.controls.adminRequired}</CardContent>
      </Card>
    );
  }

  const loadOn = telemetry?.loadOn ?? null;

  const sendLoad = async (on: boolean) => {
    setBusy(true);
    try {
      const res = await postTracerLoad(slug, on);
      showWriteResult(res.responseObject, "Load");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const sendForceLoad = async (on: boolean) => {
    setBusy(true);
    try {
      const res = await postTracerLoadForce(slug, on);
      showWriteResult(res.responseObject, "Force load");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const sendTestMode = async (on: boolean) => {
    setBusy(true);
    try {
      const res = await postTracerLoadTest(slug, on);
      showWriteResult(res.responseObject, "Test mode");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.tracer.detail.controls}</CardTitle>
        <p className="text-xs text-amber-600">⚠ {t.tracer.controls.readOnlyWarning}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-sm font-medium mb-1">
            {t.tracer.controls.manualLoad}
            {loadOn !== null ? ` (aktuálně ${loadOn ? "ON" : "OFF"})` : ""}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={loadOn === true ? "default" : "outline"}
              disabled={busy}
              onClick={() => sendLoad(true)}
            >
              ON
            </Button>
            <Button
              size="sm"
              variant={loadOn === false ? "default" : "outline"}
              disabled={busy}
              onClick={() => sendLoad(false)}
            >
              OFF
            </Button>
          </div>
        </div>

        <div>
          <div className="text-sm font-medium mb-1">{t.tracer.controls.forceLoad}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => sendForceLoad(true)}>
              Force ON
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => sendForceLoad(false)}>
              Force OFF
            </Button>
          </div>
        </div>

        <div>
          <div className="text-sm font-medium mb-1">{t.tracer.controls.testMode}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => sendTestMode(true)}>
              Test ON
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => sendTestMode(false)}>
              Test OFF
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
